import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnPrimary, btnSecondary } from '../styles/theme.js'
import { bulkHighlights as bulkApi, highlightTypes as htApi, projects as projectsApi } from '../lib/api.js'

// --- Styles ---

const pageWrap = css`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  a { color: ${colors.textSecondary}; }
`

const searchRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
`

const searchInput = css`
  ${input};
  flex: 1;
  max-width: 500px;
`

const resultsSummary = css`
  padding: ${space.md} ${space.lg};
  background: ${colors.bgSidebar};
  border-radius: 8px;
  font-size: ${font.size.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const applyBar = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.md};
  background: ${colors.brandLight};
  border-radius: 8px;
  border: 1px solid ${colors.brand};
`

const sessionGroup = css`
  ${card};
  margin-bottom: ${space.md};
`

const sessionHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${space.sm};
  cursor: pointer;
`

const sessionTitle = css`
  font-weight: ${font.weight.medium};
  font-size: ${font.size.sm};
  color: ${colors.text};
`

const matchInfo = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const messageBlock = css`
  padding: ${space.sm} ${space.md};
  margin-bottom: ${space.xs};
  border-radius: 4px;
  font-size: ${font.size.sm};
  line-height: 1.6;
`

const matchHighlight = css`
  background: ${colors.yellow};
  padding: 0 2px;
  border-radius: 2px;
`

const batchList = css`
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background: ${colors.bgCard};
  overflow: hidden;
`

const batchRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${space.md} ${space.lg};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const batchInfo = css`
  flex: 1;
`

const batchQuery = css`
  font-weight: ${font.weight.medium};
  font-size: ${font.size.sm};
`

const batchMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
`

const colorDot = css`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

// --- Component ---

export function BulkHighlightsView() {
  let projectId = null
  let project = null
  let query = ''
  let searchResults = null
  let highlightTypesList = []
  let selectedTypeId = ''
  let batches = []
  let loading = false
  let searching = false
  let applying = false
  let error = null

  async function loadData() {
    loading = true
    m.redraw()
    try {
      ;[highlightTypesList, batches] = await Promise.all([
        htApi.list(projectId),
        bulkApi.listBatches(projectId),
      ])
      if (highlightTypesList.length > 0 && !selectedTypeId) {
        selectedTypeId = highlightTypesList[0].id
      }
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function doSearch() {
    if (!query.trim()) return
    searching = true
    error = null
    searchResults = null
    m.redraw()
    try {
      searchResults = await bulkApi.search(projectId, query.trim())
    } catch (err) {
      error = err.message
    } finally {
      searching = false
      m.redraw()
    }
  }

  async function applyHighlights() {
    if (!query.trim() || !selectedTypeId) return
    applying = true
    error = null
    m.redraw()
    try {
      await bulkApi.apply(projectId, query.trim(), selectedTypeId)
      searchResults = null
      query = ''
      batches = await bulkApi.listBatches(projectId)
    } catch (err) {
      error = err.message
    } finally {
      applying = false
      m.redraw()
    }
  }

  async function undoBatch(batchId) {
    try {
      await bulkApi.undoBatch(projectId, batchId)
      batches = await bulkApi.listBatches(projectId)
      m.redraw()
    } catch (err) {
      error = err.message
      m.redraw()
    }
  }

  function renderContentWithMatches(content, matches) {
    if (!matches || matches.length === 0) return content

    const parts = []
    let lastEnd = 0
    // Show a window around matches
    for (const match of matches) {
      const contextStart = Math.max(0, match.start - 60)
      const contextEnd = Math.min(content.length, match.end + 60)

      if (contextStart > lastEnd) {
        if (lastEnd > 0) parts.push(m('span', '... '))
      }
      if (contextStart > lastEnd || lastEnd === 0) {
        parts.push(m('span', content.slice(contextStart, match.start)))
      } else if (lastEnd < match.start) {
        parts.push(m('span', content.slice(lastEnd, match.start)))
      }
      parts.push(m('span', { class: matchHighlight }, content.slice(match.start, match.end)))
      parts.push(m('span', content.slice(match.end, contextEnd)))
      lastEnd = contextEnd
    }
    if (lastEnd < content.length && parts.length > 0) parts.push(m('span', '...'))
    return parts
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadData()
    },

    view(vnode) {
      projectId = vnode.attrs.projectId

      return m('div', { class: pageWrap }, [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Bulk Highlights',
        ]),

        m('h1', { style: `font-size: ${font.size['2xl']}; font-weight: ${font.weight.bold};` },
          'Bulk Highlight Search'),

        error ? m('div', {
          style: `padding: ${space.md}; background: #fff0f0; border: 1px solid #ffcccc; border-radius: 6px; color: #cc0000; font-size: ${font.size.sm};`,
        }, error) : null,

        // Search bar
        m('div', { class: searchRow }, [
          m('input', {
            class: searchInput,
            type: 'text',
            placeholder: 'Search for text across all sessions...',
            value: query,
            oninput: (e) => { query = e.target.value },
            onkeydown: (e) => { if (e.key === 'Enter') doSearch() },
          }),
          m('button', {
            class: btnPrimary,
            disabled: searching || !query.trim(),
            onclick: doSearch,
          }, searching ? 'Searching...' : 'Search'),
        ]),

        // Search results
        searchResults ? [
          m('div', { class: resultsSummary }, [
            m('span', [
              m('strong', searchResults.totalMatches),
              ` matches across `,
              m('strong', searchResults.sessions.length),
              ` sessions for "`,
              m('em', searchResults.query),
              `"`,
            ]),
          ]),

          // Apply bar
          searchResults.totalMatches > 0 ? m('div', { class: applyBar }, [
            m('span', { style: `font-size: ${font.size.sm}; font-weight: ${font.weight.medium};` },
              'Apply highlight:'),
            m('select', {
              style: `padding: 4px 8px; border: 1px solid ${colors.border}; border-radius: 4px; font-family: ${font.family}; font-size: ${font.size.sm};`,
              value: selectedTypeId,
              onchange: (e) => { selectedTypeId = e.target.value },
            }, highlightTypesList.map(ht =>
              m('option', { value: ht.id }, ht.name)
            )),
            m('button', {
              class: btnPrimary,
              disabled: applying || !selectedTypeId,
              onclick: applyHighlights,
            }, applying
              ? 'Applying...'
              : `Apply to all ${searchResults.totalMatches} matches`),
          ]) : null,

          // Results grouped by session
          ...searchResults.sessions.slice(0, 50).map(group =>
            m('div', { key: group.sessionId, class: sessionGroup }, [
              m('div', {
                class: sessionHeader,
                onclick: () => m.route.set(`/projects/${projectId}/sessions/${group.sessionId}`),
              }, [
                m('span', { class: sessionTitle },
                  group.externalId || group.sessionId.slice(0, 8)),
                m('span', { class: matchInfo },
                  `${group.totalMatches} match${group.totalMatches === 1 ? '' : 'es'}`),
              ]),
              ...group.messages.slice(0, 5).map(msg =>
                m('div', {
                  key: msg.message_id,
                  class: messageBlock,
                  style: `background: ${msg.role === 'user' ? colors.msgUser : colors.msgAssistant};`,
                }, [
                  m('span', {
                    style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; text-transform: uppercase; display: block; margin-bottom: 2px;`,
                  }, msg.role),
                  m('div', renderContentWithMatches(msg.content, msg.matches)),
                ])
              ),
              group.messages.length > 5
                ? m('div', { style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; padding: ${space.sm};` },
                    `+ ${group.messages.length - 5} more messages with matches`)
                : null,
            ])
          ),
          searchResults.sessions.length > 50
            ? m('div', { style: `text-align: center; padding: ${space.md}; color: ${colors.textMuted}; font-size: ${font.size.sm};` },
                `Showing 50 of ${searchResults.sessions.length} sessions`)
            : null,
        ] : null,

        // Previous batches
        batches.length > 0 ? [
          m('h2', { style: `font-size: ${font.size.lg}; font-weight: ${font.weight.semibold}; margin-top: ${space.lg};` },
            'Previous Bulk Highlights'),
          m('div', { class: batchList },
            batches.map(batch =>
              m('div', { key: batch.id, class: batchRow }, [
                m('div', { class: batchInfo }, [
                  m('div', { class: batchQuery }, [
                    m('span', { class: colorDot, style: `background: ${batch.type_color};` }),
                    `"${batch.search_query}" — ${batch.type_name}`,
                  ]),
                  m('div', { class: batchMeta },
                    `${batch.match_count} highlights · ${new Date(batch.created_at).toLocaleDateString()}`),
                ]),
                m('button', {
                  class: btnSecondary,
                  style: 'font-size: 0.75rem;',
                  onclick: () => undoBatch(batch.id),
                }, 'Undo'),
              ])
            )
          ),
        ] : null,
      ])
    },
  }
}
